import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import MRMLogo from "@/assets/images/LogoMRM-HD.png";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Typography } from "@mui/material";
import { LoadingSpinner } from "@/components/ui/loading";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { setAuthState } from "@/redux/slices/auth.slice";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { RoutesEnum } from "@/routes/enum/Routes";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { api } from "@/services/instance";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { DialogRequest } from "./DialogRequest";

const formSchema = z.object({
  email: z
    .string({ message: "Email inválido." })
    .email({ message: "Formato de email inválido." }),
  password: z.string().min(8, {
    message: "Contraseña debe ser de al menos 8 letras.",
  }),
});

export const LoginForm = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleLogin = async (email: string, password: string) => {
    setLoading(true);
    try {
      await api.post(
        "/auth/login",
        {
          email,
          password,
          rememberMe,
        },
        {
          withCredentials: true,
        },
      );

      dispatch(setAuthState({ isAuthenticated: true }));
      navigate(RoutesEnum.HOME);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "No se pudo iniciar sesión. Verifica tus credenciales.";
      ErrorToast({
        message: errorMessage,
      });
      form.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className={"w-[25rem] h-[30rem] p-4"}>
        <CardHeader className="flex gap-[0.5rem] pb-[0.5rem] flex-col items-center">
          <div className="w-[4rem]">
            <img src={MRMLogo} alt="MRM Logo" />
          </div>
          <CardTitle className="font-[Montserrat] font-semibold text-[18px] text-[#1F6B45]">
            Accede a tu cuenta
          </CardTitle>
        </CardHeader>

        <CardContent className="flex flex-col gap-[0.5rem]">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(({ email, password }) => {
                handleLogin(email, password);
              })}
              className="space-y-4 flex flex-col justify-between"
            >
              <div className="min-h-[4rem] h-[4rem]">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-[Montserrat] font-semibold text-[14px] text-[#1F6B45]">
                        Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Ingresa tu email"
                          {...field}
                          className="font-[Montserrat] text-[14px] text-[#1F6B45] max-w-[100%] min-w-[100%]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="min-h-[4rem] h-[4rem]">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-[Montserrat] font-semibold text-[14px] text-[#1F6B45]">
                        Contraseña
                      </FormLabel>
                      <FormControl>
                        <div className="relative min-w-[100%] max-w-[100%]">
                          <Input
                            {...field}
                            type={showPassword ? "text" : "password"}
                            placeholder="Ingresa tu contraseña"
                            className="pr-10 font-[Montserrat] text-[14px] text-[#1F6B45]"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                            aria-label="Toggle password visibility"
                          >
                            {showPassword ? (
                              <Eye className="size-5 text-[#1F6B45]" />
                            ) : (
                              <EyeOff className="size-5 text-[#1F6B45]" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex items-center justify-between space-x-2 pt-1">
                <div className="flex items-center justify-center">
                  <label className="text-sm font-medium leading-none cursor-pointer hover:underline">
                    <span
                      className="text-[12px] font-[Montserrat] text-[#1F6B45]"
                      onClick={() => setOpen(true)}
                    >
                      ¿Olvidaste tu contraseña?
                    </span>
                  </label>
                </div>
                <div className="flex gap-1 items-center justify-center">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={(checked) =>
                      setRememberMe(checked === true)
                    }
                  />
                  <label
                    htmlFor="rememberMe"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    <p className="text-[12px] font-[Montserrat] text-[#1F6B45]">
                      Recordarme
                    </p>
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                variant="contained"
                className="hover:bg-[#1F6B45]/90 min-w-[100%] max-w-[100%] mt-4"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" className="text-white" />
                    <Typography className="text-[14px] font-[Montserrat] text-white">
                      Cargando...
                    </Typography>
                  </div>
                ) : (
                  <Typography className="text-[14px] font-[Montserrat]">
                    Iniciar sesión
                  </Typography>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
      <DialogRequest open={open} setOpen={setOpen} />
    </>
  );
};
